import React, { useCallback, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BrandLogo from "../branding/BrandLogo";
import LangSwitcher from "../LangSwitcher";
import AudioMuteButton from "./AudioMuteButton";
import ErrorPopup from "../ErrorPopup";
import {
  popToPrevNonRecordingSafeRoute,
  popToPrevSafeRoute,
} from "../../hooks/useLastSafeRoute";
import { usePresentationLabelling } from "../../hooks/usePresentationLabelling";

function NavBar({
  className = "",
  menuOpen = false,
  measureOpen = false,
  onToggleMeasure,
  onToggleMenu,
  onCloseMenu,
  guidePath,
  showAudioMute = false,
  audioMuted = false,
  onToggleAudioMuted,
  flowExitControl = null,
}) {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const [showFlowExitPopup, setShowFlowExitPopup] = useState(false);
  const { exclusivelyForClinicalInvestigations } =
    usePresentationLabelling("userWebApp");

  const path = location?.pathname || "";
  const isHomeRoute = path === "/home";

  // Logo should show on bottom-nav pages EXCEPT home
  const showLogo = path === "/home" || path === "/notice" || path === "/about";

  // Home should show neither logo nor back button
  const hideLeftControl = path === "/home";

  // back is only available if we actually have a safe previous route
  const hasSafeBack = useMemo(() => {
    return !hideLeftControl && !showLogo;
  }, [hideLeftControl, showLogo]);

  const showFlowExitButton =
    Boolean(flowExitControl) && !showLogo && !hideLeftControl;
  const showBackButton = !showLogo && !hideLeftControl && hasSafeBack;

  const handleNavigate = useCallback(() => {
    onCloseMenu?.();
  }, [onCloseMenu]);

  // inside NavBar.js
  const handleBack = useCallback(() => {
    onCloseMenu?.();

    const current = `${location?.pathname || ""}${location?.search || ""}`;

    const dest = popToPrevSafeRoute("/home", current);

    navigate(dest); // do NOT replace while debugging
  }, [navigate, onCloseMenu, location?.pathname, location?.search]);

  const closeFlowExitPopup = useCallback(() => {
    setShowFlowExitPopup(false);
  }, []);

  const handleOpenFlowExitPopup = useCallback(() => {
    onCloseMenu?.();
    setShowFlowExitPopup(true);
  }, [onCloseMenu]);

  const handleConfirmFlowExit = useCallback(async () => {
    onCloseMenu?.();
    await flowExitControl?.onConfirm?.();

    const current = `${location?.pathname || ""}${location?.search || ""}`;
    const dest = popToPrevNonRecordingSafeRoute(
      flowExitControl?.fallbackPath || "/home",
      current,
    );

    navigate(dest, { replace: true });
  }, [
    flowExitControl,
    location?.pathname,
    location?.search,
    navigate,
    onCloseMenu,
  ]);

  const flowExitPopup = useMemo(() => {
    if (!showFlowExitButton || !showFlowExitPopup) {
      return null;
    }

    return {
      title:
        flowExitControl?.title ||
        t("navBar.confirmLeaveTitle", "Leave this assessment?"),
      message:
        flowExitControl?.message ||
        t(
          "navBar.confirmLeaveBody",
          "Your progress in this assessment will be discarded. Are you sure you want to leave?",
        ),
      confirmLabel:
        flowExitControl?.confirmLabel || t("navBar.leaveAssessment", "Leave"),
      cancelLabel:
        flowExitControl?.cancelLabel || t("navBar.stayHere", "Stay here"),
      confirmIcon: flowExitControl?.confirmIcon || "close",
      dialogLabel:
        flowExitControl?.dialogLabel ||
        t("navBar.confirmLeaveTitle", "Leave this assessment?"),
      onConfirm: handleConfirmFlowExit,
    };
  }, [flowExitControl, handleConfirmFlowExit, showFlowExitButton, showFlowExitPopup, t]);

  return (
    <>
      <header
        className={` bg-background flex flex-row sm:grid sm:grid-cols-[minmax(7rem,1fr)_minmax(0,2fr)_minmax(7rem,1fr)] items-center w-full gap-4 z-50 mb-2 ${!menuOpen && !measureOpen && "md:border-b md:border-greyscale-1"} ${className}`}
      >
        {/* Left: logo OR route-exit control OR back button */}
        <div className="flex items-center gap-2 min-w-0 w-fit justify-start py-2 pl-2 md:pl-4 ">
          {showFlowExitButton ? (
            <button
              type="button"
              onClick={handleOpenFlowExitPopup}
              className="group inline-flex h-9 w-9 items-center justify-center rounded-full text-typography transition-colors hover:text-primary focus-visible:text-primary"
              aria-label={t("navBar.closeAssessment", "Close assessment")}
              title={t("navBar.closeAssessment", "Close assessment")}
            >
              <span
                className="material-symbols-outlined text-base leading-none group-hover:text-primary group-focus-visible:text-primary"
                aria-hidden="true"
              >
                close
              </span>
            </button>
          ) : showBackButton ? (
            <button
              type="button"
              onClick={handleBack}
              className="group flex !flex-row gap-1"
              aria-label={t("navBar.back", "Back")}
            >
              <span
                className="material-symbols-outlined text-typography text-sm group-hover:text-primary group-focus-visible:text-primary"
                aria-hidden="true"
              >
                arrow_back_ios
              </span>
            </button>
          ) : showLogo ? (
            <Link
              className="flex-shrink-0 w-fit"
              to="/home"
              aria-label="Home"
              onClick={handleNavigate}
            >
              <BrandLogo
                alt="pDx"
                className="h-12 w-auto max-w-none title-text"
              />
            </Link>
          ) : (
            <div className="h-12 w-[120px]" aria-hidden="true" />
          )}
        </div>

        {/* CENTER (desktop nav perfectly centered in viewport) */}
        <div className="flex flex-1 flex-col items-center justify-center gap-1 w-full px-2 min-w-0">
          {isHomeRoute ? (
            exclusivelyForClinicalInvestigations ? (
              <p className="muted-text text-center text-xs leading-tight sm:text-sm md:pointer-events-none md:fixed md:bottom-4 md:right-4 md:z-40 md:max-w-xs md:text-right">
                {t(
                  "labelling.investigationOnly",
                  "Exclusively for clinical investigations",
                )}
              </p>
            ) : null
          ) : null}

          <nav className="hidden md:flex items-center justify-center gap-8 self-center w-full max-w-2xl whitespace-nowrap lg:gap-10 xl:gap-12">
            {/* Home */}
            <Link to="/home" className="nav-title" onClick={onCloseMenu}>
              {t("navBar.home", "Home")}
            </Link>

            {/* To Do */}
            <Link to="/todo" className="nav-title" onClick={onCloseMenu}>
              {t("navBar.todo", "To Do")}
            </Link>

            {/* Measure toggle */}
            <button
              type="button"
              className={["nav-title", measureOpen ? "text-primary" : ""].join(
                " ",
              )}
              onClick={onToggleMeasure}
              aria-expanded={measureOpen}
              aria-label={t("navBar.measure", "Measure")}
            >
              {t("navBar.measure", "Measure")}
            </button>

            {/* Results */}
            <Link to="/results" className="nav-title" onClick={onCloseMenu}>
              {t("navBar.results", "Results")}
            </Link>

            {/* Menu toggle */}
            <button
              type="button"
              className={["nav-title", menuOpen ? "text-primary" : ""].join(
                " ",
              )}
              onClick={onToggleMenu}
              aria-expanded={menuOpen}
              aria-label={t("navBar.menu", "Menu")}
            >
              {t("navBar.menu", "Menu")}
            </button>
          </nav>
          {/* <div className="w-full flex flex-col items-center justify-center">
            {" "}
            <Link
              className="flex-shrink-0 w-fit"
              to="/home"
              aria-label="Home"
              onClick={handleNavigate}
            >
              <img
                src="/purpledx_primary_logo.png"
                alt="pDx"
                className="h-12 w-auto max-w-none title-text"
                loading="lazy"
              />
            </Link>
          </div> */}
        </div>

        {/* Contextual audio mute + Guide link (only visible on /recording/<variant>/camera) */}
        <div className="self-center hidden md:flex items-center justify-end gap-2 p-1 pr-2 ">
          {showAudioMute && (
            <AudioMuteButton muted={audioMuted} onToggle={onToggleAudioMuted} />
          )}
          {guidePath && (
            <Link
              to={guidePath}
              className="small-outlined-button group flex !flex-row gap-1 !w-fit"
            >
              <span
                className="material-symbols-outlined text-primary text-xs
                  group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
              >
                info
              </span>{" "}
              {t("navBar.guide", "Guide")}
            </Link>
          )}
          <LangSwitcher />
        </div>

        {/* Mobile right: audio mute + Guide CTA + hamburger */}
        <div className="md:hidden flex flex-row gap-2 w-fit items-center justify-end p-1 pr-2 ">
          {showAudioMute && (
            <AudioMuteButton muted={audioMuted} onToggle={onToggleAudioMuted} />
          )}
          {guidePath && (
            <Link
              to={guidePath}
              className="small-outlined-button md:hidden group flex !flex-row gap-1 !w-fit whitespace-nowrap"
            >
              <span
                className="material-symbols-outlined text-primary text-xs
                  group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
              >
                info
              </span>
              {t("navBar.guide", "Guide")}
            </Link>
          )}
          <LangSwitcher />
        </div>
      </header>
      <ErrorPopup popup={flowExitPopup} onClose={closeFlowExitPopup} />
    </>
  );
}

export default React.memo(NavBar);

// // src/components/NavBar.js
// /* ------------------------------------------------------------------ */
// /* NavBar – responsive top-level navigation                            */
// /* ------------------------------------------------------------------ */
// /**
//  * Behaviour
//  * ----------
//  * • **Mobile (< md)** – hamburger toggles a slide-down, full-height overlay
//  *   containing links: Home · Assessment · Questionnaires · Results · Sign out.
//  * • **Desktop (≥ md)** – keeps inline links and icons.
//  *
//  * @param {object}  props
//  * @param {string=} props.className  Extra Tailwind classes for the `<header>`.
//  * @returns {JSX.Element}
//  */

// import React, { useState, useCallback, useMemo } from "react";
// import { Link, useLocation, useNavigate } from "react-router-dom";
// import { getAuth, signOut } from "firebase/auth";
// import { firebaseApp } from "../../firebase/firebaseApp";
// import { HomeIcon } from "../icons/HomeIcon";
// import { LogoutIcon } from "../icons/LogoutIcon";
// import { CloseIcon } from "../icons/CloseIcon";
// import { HamburgerIcon } from "../icons/HamburgerIcon";
// import { useTranslation } from "react-i18next";
// import LangSwitcher from "../LangSwitcher";
// import { useAuth } from "../../context/AuthContext";
// import { useProfilePrefetch } from "../../context/ProfilePrefetchContext";
// import { getGuidePathFromCameraPath } from "../../utils/navigation/recordingGuideNav";

// /* ------------------------------------------------------------------ */
// /* Component                                                          */
// /* ------------------------------------------------------------------ */
// function NavBar({ className = "" }) {
//   const { t } = useTranslation();

//   const [menuOpen, setMenuOpen] = useState(false);
//   const navigate = useNavigate();
//   const location = useLocation();

//   const { user } = useAuth();
//   const { clearAllProfiles, clearProfile } = useProfilePrefetch();

//   /**
//    * Guide CTA is shown only on recording camera routes:
//    *   /recording/<variant>/camera
//    * and links to the corresponding:
//    *   /recording/<variant>/guide
//    */
//   const guidePath = useMemo(() => {
//     return getGuidePathFromCameraPath(location?.pathname || "");
//   }, [location?.pathname]);

//   const handleSignOut = useCallback(() => {
//     // Clear local caches first (best-effort)
//     try {
//       if (clearAllProfiles) clearAllProfiles();
//       else if (user?.uid) clearProfile(user.uid);
//     } catch {
//       // ignore
//     }

//     signOut(getAuth(firebaseApp))
//       .then(() => navigate("/auth/login", { replace: true }))
//       .catch(
//         (err) => process.env.NODE_ENV === "development" && console.error(err),
//       );
//   }, [navigate, clearAllProfiles, clearProfile, user?.uid]);

//   /** Close the slide-down menu after a navigation click */
//   const handleNavigate = useCallback(() => setMenuOpen(false), []);

//   return (
//     <>
//       {/* ── Top bar ─────────────────────────────────────────────────── */}
//       <header
//         className={`flex items-center justify-between w-full p-2 gap-4 z-50 ${className}`}
//       >
//         {/* Logo */}
//         <Link
//           className="flex-shrink-0 w-fit"
//           to="/home"
//           aria-label="PDX Home"
//           onClick={handleNavigate}
//         >
//           <img
//             src="/pdx_logo.png"
//             alt="PDX logo"
//             className="h-12 w-auto max-w-none"
//             loading="lazy"
//           />
//         </Link>

//         {/* Desktop icons and links */}
//         <nav className="hidden md:flex items-center gap-4">
//           {/* Desktop links */}
//           <Link to="/questionnaires" className="nav-title min-h-[44px]">
//             {t("navBar.questionnaires")}
//           </Link>
//           <Link to="/about" className="nav-title">
//             {t("navBar.about")}
//           </Link>
//           <Link to="/results" className="nav-title">
//             {t("navBar.results")}
//           </Link>
//           <Link to="/profile" className="nav-title min-h-[44px]">
//             {t("navBar.profile", "Profile")}
//           </Link>
//           {/* Contextual Guide link (only visible on /recording/<variant>/camera) */}
//           {guidePath && (
//             <Link
//               to={guidePath}
//               className="small-outlined-button group flex !flex-row gap-1"
//             >
//               <span
//                 className="material-symbols-outlined text-primary text-xs
//                   group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
//               >
//                 info
//               </span>{" "}
//               {t("navBar.guide", "Guide")}
//             </Link>
//           )}
//           {/* Desktop icons  */}
//           <LangSwitcher />

//           <Link to="/home" aria-label="Home">
//             <HomeIcon />
//           </Link>
//           <button
//             type="button"
//             onClick={handleSignOut}
//             aria-label="Sign out"
//             className="focus:outline-none"
//           >
//             <LogoutIcon />
//           </button>
//         </nav>

//         {/* Contextual Guide link (only visible on /recording/<variant>/camera) */}
//         <div className="md:hidden flex flex-row gap-4 w-full items-center justify-end">
//           {guidePath && (
//             <Link
//               to={guidePath}
//               className="small-outlined-button md:hidden group flex !flex-row gap-1"
//             >
//               <span
//                 className="material-symbols-outlined text-primary text-xs
//                   group-hover:text-greyscale-1 group-focus-visible:text-greyscale-1"
//               >
//                 info
//               </span>
//               {t("navBar.guide", "Guide")}
//             </Link>
//           )}
//         </div>
//         {/* Hamburger (mobile) */}
//         <button
//           className="md:hidden focus:outline-none"
//           onClick={() => setMenuOpen((o) => !o)}
//           aria-label={menuOpen ? "Close menu" : "Open menu"}
//         >
//           {menuOpen ? <CloseIcon /> : <HamburgerIcon />}
//         </button>
//       </header>

//       {/* ── Slide-down overlay (mobile) ─────────────────────────────── */}
//       {menuOpen && (
//         <div
//           className="fixed inset-0 z-40 bg-white flex flex-col items-start
//                      py-24 p-8 gap-4 animate-[slideDown_0.25s_ease-out] md:hidden"
//         >
//           <LangSwitcher showLabelWhenClosed={true} />

//           <Link
//             to="/home"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.home")}
//           </Link>
//           <Link
//             to="/recording"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.assessment")}
//           </Link>
//           <Link
//             to="/questionnaires"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.questionnaires")}
//           </Link>
//           <Link
//             to="/about"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.about")}
//           </Link>
//           <Link
//             to="/results"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.results")}
//           </Link>
//           <Link
//             to="/profile"
//             onClick={handleNavigate}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.profile", "Profile")}
//           </Link>
//           <button
//             type="button"
//             onClick={() => {
//               handleSignOut();
//               setMenuOpen(false);
//             }}
//             className="nav-menu-text-button"
//           >
//             {t("navBar.signOut")}
//           </button>
//         </div>
//       )}

//       {/* ── Slide-down keyframe ────────────────────────────────────── */}
//       <style>{`
//         @keyframes slideDown {
//           from { transform: translateY(-100%); }
//           to   { transform: translateY(0); }
//         }
//       `}</style>
//     </>
//   );
// }

// export default React.memo(NavBar);
